import { App as CapacitorApp } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IonApp } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { useEffect, useState } from 'react';
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch';
import { BackgroundTask } from '@capawesome/capacitor-background-task';
import { Capacitor } from '@capacitor/core';

import { resetAppStateAsync, setIsBackgroundState, setIsDebugState, setNetworkConnectedState, setStateIdState } from './store/appSlice';
import { createUUID, delay, infiniteTimer } from './common/util';
import { FormSpinner } from './components/formSpinner/FormSpinner';
import Warning from './components/warning/Warning';
import PinInPage from './pages/signinPage/pinInPage/PinInPage';
import Private from './routes/private/Private';
import Public from './routes/public/Public';
import store from './store';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { logTransaction } from './common/log';
import { ClockingDirection } from './models/dms/clocking.model';
import { LogType } from './models/log.model';
import { ICompany } from './models/portal/company.model';
import { IDatabase } from './models/portal/database.model';
import AuthErrorProvider from './providers/AuthErrorProvider';
import ClockOffAttendanceModalProvider from './providers/ClockOffAttendanceModalProvider';
import ClockOffLabourModalProvider from './providers/ClockOffLabourModalProvider';
import { useLocation } from './providers/LocationProvider';
import { setSelectedCompanyStoreAsync } from './store/companySlice';
import { setSelectedDatabaseStoreAsync } from './store/databaseSlice';
import { setSelectedUsernameStoreAsync } from './store/userSlice';
import { useDataSync } from './providers/DataSyncProvider';
import { LoadingMessage } from './components/loadingMessage/LoadingMessage';
import { setSelectedDealerCodeState, signOutAsync } from './store/authSlice';
import OtpModalProvider from './providers/OtpModalProvider';
import { getDatabasesByCompanyNumber } from './api/portal/database.api';
import { reInitialiseDmsAxios } from './api/dms/axiosDmsInstance';
import { reInitialisePortalAxios } from './api/portal/axiosPortalInstance';
import { IAppConfigType } from './models/appConfig.model';
import MediaModalProvider from './providers/MediaModalProvider';

export let isDebugRef = false;
export let enableDebugLogRef = false;
export let enableGeolocationLogRef = false;
export let networkConnectedRef = true;
export let isBackgroundRef = true;

export const setIsDebug = (state: boolean) => {
  isDebugRef = state;
};
export const setEnableDebugLog = (state: boolean) => {
  enableDebugLogRef = state;
};
export const setEnableGeolocationLog = (state: boolean) => {
  enableGeolocationLogRef = state;
};
export const setNetworkConnected = (state: boolean) => {
  networkConnectedRef = state;
};
export const setIsBackground = (state: boolean) => {
  isBackgroundRef = state;
};

let isSetup = false;

const App: React.FC = () => {
  const [setupComplete, setSetupComplete] = useState(false);
  const { isDarkMode } = useAppSelector((state) => state.settings);
  const { isAuthenticated, technician } = useAppSelector((state) => state.auth);
  const { networkConnected, isBackground } = useAppSelector((state) => state.app);
  const {
    actions: { pauseLocationTracking, resumeLocationTracking },
  } = useLocation();
  const {
    actions: { getRequestQueueItems, processRequestQueue },
  } = useDataSync();
  const { clockingQueue, techCommentQueue, vehicleAttachmentQueue } = useAppSelector((state) => state.requestQueue);
  const { hasAskedPermision } = useAppSelector((state) => state.location);
  // const { actions: { clearAll, scheduleDistanceTrackingEnabled, scheduleAttendanceClockingReminder, scheduleSyncInProgress } } = useNotification();

  const dispatch = useAppDispatch();

  useEffect(() => {
    // Set up application
    const setup = async () => {
      if (!isSetup) {
        isSetup = true;
        await logTransaction(LogType.Info, 'App setup starting');

        if (isDarkMode) {
          document.body.classList.add('dark');
          try {
            await StatusBar.setStyle({ style: Style.Light }).catch(() => {});
            await StatusBar.setBackgroundColor({ color: '#042846' });
          } catch (ex) {}
        } else {
          document.body.classList.remove('dark');
          try {
            await StatusBar.setStyle({ style: Style.Dark });
            await StatusBar.setBackgroundColor({ color: '#68b8fc' });
          } catch (ex) {}
        }

        if (process.env.REACT_APP_PRODUCTION === 'false' || process.env.REACT_APP_DEBUG === 'true') {
          await dispatch(setIsDebugState(true));
        }

        // TEST
        // dispatch(setIsDarkModeStoreAsync(false));
        // dispatch(setIsDarkModeStoreAsync(false));
        // dispatch(setIsDarkModeStoreAsync(false));
        // dispatch(setIsDarkModeStoreAsync(false));
        // dispatch(setIsDarkModeStoreAsync(false));
        // dispatch(setIsDarkModeStoreAsync(false));
        // dispatch(setIsDarkModeStoreAsync(false));
        // END TEST

        await dispatch(resetAppStateAsync({ clearAll: false }));

        // Configure the stateId
        const stateId = createUUID();
        window.localStorage.setItem('stateId', stateId);
        await dispatch(setStateIdState(stateId));

        SplashScreen.hide();

        // Auto login
        if (process.env.REACT_APP_AUTOLOGIN === 'true') {
          if (process.env.REACT_APP_PRODUCTION === 'false') {
            const companyData = {
              id: 1,
              number: 0,
              code: 'AIT',
              name1: 'Auto-IT Auto Apps Administration',
              configs: [
                { name: process.env.REACT_APP_PILOTCONFIG, value: 'N' },
                // Replace with password
                { name: 'autologin', value: '' },
              ],
            } as ICompany;
            await dispatch(setSelectedDealerCodeState('f27ab62c'));
            await dispatch(setSelectedCompanyStoreAsync(companyData));
            await dispatch(setSelectedDatabaseStoreAsync({ dbId: 95, dbDisplayName: 'Test 2085 (WR-0018-P)', registryAlias: 'Test 2085 1', subModules: [] } as IDatabase));
            await dispatch(setSelectedUsernameStoreAsync('usupport'));
          } else {
            const companyData = {
              id: 18,
              number: 14,
              code: 'SBMOTORS',
              name1: 'Sandbox Motors',
              configs: [
                { name: process.env.REACT_APP_PILOTCONFIG, value: 'N' },
                // Replace with password
                { name: 'autologin', value: '' },
              ],
            } as ICompany;
            await dispatch(setSelectedDealerCodeState('9576e940'));
            await dispatch(setSelectedCompanyStoreAsync(companyData));
            await dispatch(setSelectedDatabaseStoreAsync({ dbId: 264, dbDisplayName: 'Sandbox AUS Car 2085 (WR-0018P)', registryAlias: 'AUS Car', subModules: [] } as IDatabase));
            await dispatch(setSelectedUsernameStoreAsync('usupport'));
          }
        }

        const { selectedCompany } = store.getState().company;
        if (selectedCompany) {
          const isTest = (selectedCompany.configs?.find((c) => c.name === process.env.REACT_APP_TESTCONFIG)?.value ?? 'N') === 'Y' ? true : false;
          const isPilot = (selectedCompany.configs?.find((c) => c.name === process.env.REACT_APP_PILOTCONFIG)?.value ?? 'N') === 'Y' ? true : false;
          await reInitialiseDmsAxios(isTest ? IAppConfigType.TEST : isPilot ? IAppConfigType.PILOT : IAppConfigType.PROD);
          await reInitialisePortalAxios(isTest ? IAppConfigType.TEST : isPilot ? IAppConfigType.PILOT : IAppConfigType.PROD);
        }

        setSetupComplete(true);

        // Watch online/offline status
        Network.addListener('networkStatusChange', (status) => {
          if (status.connected) {
            dispatch(setNetworkConnectedState(true));
          } else {
            dispatch(setNetworkConnectedState(false));
          }
        });

        // Watch foreground/background mode
        CapacitorApp.addListener('appStateChange', async (state) => {
          dispatch(setIsBackgroundState(!state.isActive));

          if (state.isActive) {
            logTransaction(LogType.Info, 'App foreground');

            const { technician } = store.getState().auth;
            const { distanceTrackingUserActivated, gpsWatchStarting } = store.getState().settings;

            try {
              const networkStatus = await Network.getStatus();
              if (networkStatus.connected) {
                dispatch(setNetworkConnectedState(true));
              }
            } catch (err) {
              dispatch(setNetworkConnectedState(true));
            }

            // Start background geolocation services
            // Background geolocation services should only have been disabled if distance was NOT being tracked
            if (technician?.code && !distanceTrackingUserActivated && !gpsWatchStarting && hasAskedPermision) {
              await resumeLocationTracking();
            }

            // Clear notifications
            // clearAll();
          } else {
            logTransaction(LogType.Info, 'App background');
            dispatch(setNetworkConnectedState(false));

            const { distanceTrackingUserActivated } = store.getState().settings;
            const { attendanceClocking } = store.getState().technicianClocking;
            const { scheduledEndTime } = store.getState().settings;
            const { clockingQueue, techCommentQueue, vehicleAttachmentQueue } = store.getState().requestQueue;
            const { technician } = store.getState().auth;

            // Start background geolocation services
            // Background geolocation services should only have been disabled if distance was NOT being tracked
            if (technician?.code && !distanceTrackingUserActivated) {
              await pauseLocationTracking();
            }

            // Show notifications
            // If distance tracking is enabled
            if (distanceTrackingUserActivated) {
              // scheduleDistanceTrackingEnabled();
            }
            // If Attendace is still on at the end of the scheduled day
            if (attendanceClocking?.direction === ClockingDirection.On && scheduledEndTime) {
              // Scheduled end time date will be some random date. Normalise the date. Time is what we're after.
              //const scheduledEndTimeMoment = getStateStringAsMoment(scheduledEndTime);
              //const normalisedScheduledEndTimeMoment = moment().set({ hour: scheduledEndTimeMoment.hour(), minute: scheduledEndTimeMoment.minute(), second: 0 });
              // scheduleAttendanceClockingReminder(normalisedScheduledEndTimeMoment.toDate());
            }
            // If a sync is currently in progress
            if (clockingQueue?.length || techCommentQueue?.length || vehicleAttachmentQueue?.length) {
              // scheduleSyncInProgress((clockingQueue?.length || 0) + (techCommentQueue?.length || 0) + (vehicleAttachmentQueue?.length || 0));
            }
          }
        });

        /* 
        When the app is inactive, the device may kill the app or put it into standby.
        This will impact clocking if the user clocks and immediately closes the app.

        iOS
        A package called @capawesome/capacitor-background-task is available for iOS (still in dev for Android).
        This package allows for a task to run before the app is killed.
        This is implemented into the iOS version below.

        Android
        A package called @transistorsoft/capacitor-background-fetch is available for both iOS and Android.
        This package allows the app to "wake up" every 15 minutes or so to run tasks.
        This is implemented into the Android and iOS version below
        */

        const platform = Capacitor.getPlatform();

        if (platform === 'android') {
          // Android implementation using @transistorsoft/capacitor-background-fetch
          await enableBackgroundFetch();
        } else if (platform === 'ios') {
          // iOS implementation using @capawesome/capacitor-background-task
          await enableBackgroundTask();
          // iOS implementation using @transistorsoft/capacitor-background-fetch
          await enableBackgroundFetch();
        }

        // Set up a module polling service that incrementally checks if the database still has access to the module
        const moduleTimer = infiniteTimer(5 * 60 * 1000, async () => {
          const module = process.env.REACT_APP_MODULE_CODE;
          const { isAuthenticated } = store.getState().auth;
          const { selectedCompany } = store.getState().company;
          const { selectedDatabase } = store.getState().database;
          if (isAuthenticated && process.env.REACT_APP_DEBUG === 'false') {
            // Selected company must exist
            if (selectedCompany?.number === undefined || selectedDatabase?.dbId === undefined) {
              // No company or database found
              logTransaction(LogType.Error, `While checking for available module ${module}, ${selectedCompany?.number === undefined ? 'no selected company found' : ''} ${selectedDatabase?.dbId === undefined ? 'no selected database found' : ''}`);
              await dispatch(signOutAsync());
            } else {
              try {
                const availableDatabases = await getDatabasesByCompanyNumber(selectedCompany.number.toString());
                const matchingDatabase = availableDatabases.find((d) => d.dbId === selectedDatabase.dbId);
                if (matchingDatabase?.dbId === undefined) {
                  // No database found so module does not exists
                  logTransaction(
                    LogType.Warn,
                    `While checking for available module ${module}, no matching databases found for selected company number ${selectedCompany.number} (${selectedCompany.name1}) and selected database ${selectedDatabase.dbId} (${selectedDatabase.dbDisplayName})`,
                  );
                  await dispatch(signOutAsync());
                }
              } catch (ex: any) {
                // Failed to check module
              }
            }
          }
        });
        moduleTimer.start();

        logTransaction(LogType.Info, 'App setup complete');
      }
    };

    setup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableBackgroundFetch = async () => {
    // Enable background fetch
    try {
      logTransaction(LogType.Info, 'BackgroundFetch enabling...');
      const status = await BackgroundFetch.configure(
        {
          minimumFetchInterval: 15,
        },
        async (taskId) => {
          logTransaction(LogType.Info, `BackgroundFetch EVENT: ${taskId}`);

          // All we need to do is wait for the already running data sync provider to complete its work by
          // continually checking the queue and wait for the queue to be empty.
          logTransaction(LogType.Info, `Android BackgroundFetch tasks started`);
          await waitForQueueToComplete();
          logTransaction(LogType.Info, `Android BackgroundFetch tasks complete`);

          logTransaction(LogType.Info, `BackgroundFetch work complete: ${taskId}`);
          // Signal to the OS that work is complete
          BackgroundFetch.finish(taskId);
        },
        async (taskId) => {
          // The OS has signalled that remaining background time has expired.
          // Complete work and signal finish
          logTransaction(LogType.Info, `BackgroundFetch TIMEOUT: ${taskId}`);
          // Signal to the OS that work is complete
          BackgroundFetch.finish(taskId);
        },
      );

      // Checking BackgroundFetch status:
      if (status !== BackgroundFetch.STATUS_AVAILABLE) {
        if (status === BackgroundFetch.STATUS_DENIED) {
          logTransaction(LogType.Error, `The user explicitly disabled background behavior for this app or for the whole system`);
        } else if (status === BackgroundFetch.STATUS_RESTRICTED) {
          logTransaction(LogType.Error, `Background updates are unavailable and the user cannot enable them again`);
        }
      }
    } catch (ex: any) {
      logTransaction(LogType.Error, 'BackgroundFetch failed to enable');
    }
  };

  const enableBackgroundTask = async () => {
    logTransaction(LogType.Info, `Configuring app state change for background task`);
    CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        return;
      }
      // The app state has been changed to inactive.
      logTransaction(LogType.Info, `App is in background for iOS. Starting background before exit tasks`);

      const taskId = await BackgroundTask.beforeExit(async () => {
        // All we need to do is wait for the already running data sync provider to complete its work by
        // continually checking the queue and wait for the queue to be empty.
        logTransaction(LogType.Info, `iOS before exit tasks started`);
        await waitForQueueToComplete();
        logTransaction(LogType.Info, `iOS before exit tasks complete`);

        // Finish the background task as soon as everything is done
        BackgroundTask.finish({ taskId });
      });
    });
  };

  const waitForQueueToComplete = async () => {
    const queuePromise = new Promise<boolean>(async (res) => {
      logTransaction(LogType.Info, `Checking request queue for incomplete items`);
      let requestQueue = getRequestQueueItems();
      while (requestQueue.length) {
        logTransaction(LogType.Info, `Found ${requestQueue.length} queued items, waiting...`);
        await delay(5000);
        await processRequestQueue();
        requestQueue = getRequestQueueItems();
      }

      res(true);
    });
    return queuePromise;
  };

  return (
    <>
      <IonApp className={`${networkConnected ? 'online' : 'offline'} ${isBackground ? 'background' : 'foreground'} ${clockingQueue?.length || techCommentQueue?.length || vehicleAttachmentQueue?.length ? 'sync' : 'notsync'}`}>
        <FormSpinner />
        <IonReactRouter>
          <AuthErrorProvider store={store}>
            <OtpModalProvider>
              <ClockOffLabourModalProvider>
                <ClockOffAttendanceModalProvider>
                  <MediaModalProvider>
                    <LoadingMessage isOpen={!setupComplete} message={'loading app'} />
                    {setupComplete ? isAuthenticated ? technician?.code ? <Private /> : <PinInPage /> : <Public /> : <></>}
                  </MediaModalProvider>
                </ClockOffAttendanceModalProvider>
              </ClockOffLabourModalProvider>
            </OtpModalProvider>
          </AuthErrorProvider>
        </IonReactRouter>
      </IonApp>
      <Warning />
    </>
  );
};

export default App;
